import React, { useEffect, useRef } from 'react';
import { ArrowRight, Phone } from 'lucide-react';
import { heroContent, companyInfo, logoAltUrl } from '../data/mock';

const HeroSection = () => {
  const lineRef = useRef(null);

  useEffect(() => {
    // Animated line effect
    const line = lineRef.current;
    if (line) {
      line.style.width = '0%';
      setTimeout(() => {
        line.style.width = '100%';
      }, 500);
    }
  }, []);

  return (
    <section className="relative min-h-screen bg-black flex items-center overflow-hidden">
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-black to-neutral-900 opacity-90" />
      
      {/* Animated grid pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div 
          className="w-full h-full"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}
        />
      </div>

      {/* Electric current lines - animated */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Horizontal electric lines */}
        <div className="absolute top-1/4 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-electric-yellow/30 to-transparent animate-electric-horizontal" />
        <div className="absolute top-2/3 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-electric-yellow/20 to-transparent animate-electric-horizontal-reverse" />
        
        {/* Vertical electric lines */}
        <div className="absolute left-1/4 top-0 w-[1px] h-full bg-gradient-to-b from-transparent via-electric-yellow/20 to-transparent animate-electric-vertical" />
        <div className="absolute right-1/3 top-0 w-[1px] h-full bg-gradient-to-b from-transparent via-electric-yellow/15 to-transparent animate-electric-vertical" style={{ animationDelay: '0.5s' }} />
      </div>

      {/* Floating logo element with electric glow */}
      <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-[0.08] hidden lg:block">
        <img 
          src={logoAltUrl}
          alt=""
          className="w-[600px] h-auto animate-electric-flicker"
        />
      </div>

      {/* Electric spark decorations */}
      <div className="absolute top-20 left-10 w-1 h-1 bg-electric-yellow rounded-full animate-ping opacity-60" style={{ animationDuration: '2s' }} />
      <div className="absolute top-40 right-20 w-1.5 h-1.5 bg-electric-yellow rounded-full animate-ping opacity-40" style={{ animationDuration: '3s', animationDelay: '1s' }} />
      <div className="absolute bottom-32 left-1/4 w-1 h-1 bg-electric-yellow rounded-full animate-ping opacity-50" style={{ animationDuration: '2.5s', animationDelay: '0.5s' }} />

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-[7.6923%] pt-20">
        <div className="max-w-3xl">
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-8">
            <div 
              ref={lineRef}
              className="h-[2px] bg-electric-yellow transition-all duration-1000 ease-out"
              style={{ width: '0%', maxWidth: '60px' }}
            />
            <span className="text-electric-yellow text-sm font-medium tracking-widest uppercase">
              {companyInfo.coverage}
            </span>
          </div>

          {/* Headline */}
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.1] mb-6">
            {heroContent.headline.split('.').map((part, i) => (
              <span key={i}>
                {part}
                {i === 0 && <span className="text-electric-yellow animate-electric-flicker">.</span>}
                {i === 0 && <br />}
              </span>
            ))}
          </h1>

          {/* Subheadline */}
          <p className="text-xl md:text-2xl text-neutral-400 leading-relaxed mb-12 max-w-2xl">
            {heroContent.subheadline}
          </p>

          {/* CTAs with electric glow on hover */}
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="#contact"
              className="group inline-flex items-center justify-between gap-4 bg-electric-yellow text-black px-8 py-4 text-lg font-medium hover:bg-electric-yellow/10 hover:text-electric-yellow border-2 border-electric-yellow transition-all duration-400 hover:shadow-[0_0_20px_rgba(255,214,0,0.3)]"
            >
              {heroContent.ctaPrimary}
              <ArrowRight 
                size={20} 
                className="transform group-hover:translate-x-1 transition-transform duration-300"
              />
            </a>
            <a
              href={`tel:${companyInfo.phone}`}
              className="group inline-flex items-center justify-center gap-3 bg-white/5 text-white px-8 py-4 text-lg font-medium hover:bg-white hover:text-black border border-white/20 transition-all duration-400"
            >
              <Phone size={20} />
              {heroContent.ctaSecondary}
            </a>
          </div>

          {/* Trust badges */}
          <div className="flex items-center gap-8 mt-16 pt-8 border-t border-white/10">
            <div className="text-center">
              <div className="text-3xl font-bold text-white">15+</div>
              <div className="text-sm text-neutral-500">Years Experience</div>
            </div>
            <div className="w-px h-12 bg-white/10" />
            <div className="text-center">
              <div className="text-3xl font-bold text-white">5000+</div>
              <div className="text-sm text-neutral-500">Projects Completed</div>
            </div>
            <div className="w-px h-12 bg-white/10" />
            <div className="text-center">
              <div className="text-3xl font-bold text-white">100%</div>
              <div className="text-sm text-neutral-500">Code Compliant</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator with electric pulse */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <div className="w-px h-8 bg-gradient-to-b from-transparent to-electric-yellow animate-pulse" />
        <div className="w-2 h-2 bg-electric-yellow rounded-full animate-electric-glow" />
      </div>
    </section>
  );
};

export default HeroSection;
